import { useAudioContext } from "../use-audio-context";
import { useCallback, useMemo } from "react";
import { IAudioControls } from "./use-audio.types";
import {
    buildAudioSnapshot,
    recordAudioControlBreadcrumb,
    recordSkippedControlWithoutAudioRef,
    reportSuspectSeekToSentry,
    scheduleAudioControlVerification,
} from "../../audio-sentry-instrumentation/audio-sentry-instrumentation.utilities";
import { AudioControlAction } from "../../audio-sentry-instrumentation/audio-sentry-instrumentation.constants";
/**
 * Perform actions on the audioRef from useAudioContext.
 * @returns controls that manipulate the audioRef i.e pause, play, seek, playbackRate, volume, skipBackward, skipForward, toggleMute.
 */
const useAudio = () => {
    const { audioRef } = useAudioContext();

    const pause = useCallback(() => {
        if (audioRef?.current) {
            audioRef?.current.pause();
        }
    }, [audioRef]);

    const play = useCallback(async () => {
        if (audioRef?.current) {
            await audioRef?.current.play();
        }
    }, [audioRef]);

    const seek = useCallback(
        (time: number) => {
            if (!audioRef?.current) {
                recordSkippedControlWithoutAudioRef(AudioControlAction.SEEK);
                return;
            }

            const before = buildAudioSnapshot(audioRef.current);
            audioRef.current.currentTime = time;
            const afterSync = buildAudioSnapshot(audioRef.current);

            const context = {
                action: AudioControlAction.SEEK,
                payload: time,
                before,
                afterSync,
            };

            recordAudioControlBreadcrumb(context);
            reportSuspectSeekToSentry(context);
            scheduleAudioControlVerification({
                action: AudioControlAction.SEEK,
                payload: time,
                audio: audioRef.current,
            });
        },
        [audioRef]
    );

    const playbackRate = useCallback(
        (rate: number) => {
            if (audioRef?.current) {
                audioRef.current.playbackRate = rate;
            }
        },
        [audioRef]
    );

    const volume = useCallback(
        (volume: number) => {
            if (audioRef?.current) {
                audioRef.current.volume = volume;
            }
        },
        [audioRef]
    );

    const skipBackward = useCallback(
        (seconds: number) => {
            if (!audioRef?.current) {
                recordSkippedControlWithoutAudioRef(AudioControlAction.SKIP_BACKWARD);
                return;
            }

            const before = buildAudioSnapshot(audioRef.current);
            audioRef.current.currentTime -= seconds;
            const afterSync = buildAudioSnapshot(audioRef.current);

            const context = {
                action: AudioControlAction.SKIP_BACKWARD,
                payload: seconds,
                before,
                afterSync,
            };

            recordAudioControlBreadcrumb(context);
            reportSuspectSeekToSentry(context);
            scheduleAudioControlVerification({
                action: AudioControlAction.SKIP_BACKWARD,
                payload: seconds,
                audio: audioRef.current,
            });
        },
        [audioRef]
    );

    const skipForward = useCallback(
        (seconds: number) => {
            if (!audioRef?.current) {
                recordSkippedControlWithoutAudioRef(AudioControlAction.SKIP_FORWARD);
                return;
            }

            const before = buildAudioSnapshot(audioRef.current);
            audioRef.current.currentTime += seconds;
            const afterSync = buildAudioSnapshot(audioRef.current);

            const context = {
                action: AudioControlAction.SKIP_FORWARD,
                payload: seconds,
                before,
                afterSync,
            };

            recordAudioControlBreadcrumb(context);
            reportSuspectSeekToSentry(context);
            scheduleAudioControlVerification({
                action: AudioControlAction.SKIP_FORWARD,
                payload: seconds,
                audio: audioRef.current,
            });
        },
        [audioRef]
    );

    const toggleMute = useCallback(() => {
        if (audioRef?.current) {
            audioRef.current.muted = !audioRef?.current.muted;

            if (audioRef.current.muted) {
                audioRef.current.volume = 0;
            } else {
                audioRef.current.volume = 1;
            }
        }
    }, [audioRef]);

    const controls: IAudioControls = useMemo(
        () => ({
            pause,
            play,
            seek,
            playbackRate,
            volume,
            skipBackward,
            skipForward,
            toggleMute,
        }),
        [pause, play, seek, playbackRate, volume, skipBackward, skipForward, toggleMute]
    );

    return controls;
};

export default useAudio;
